import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Cookies from "js-cookie";
import axios from "axios";
import ProfilePage from "./ProfilePanel/ProfilePage";
import ProfileHeader from "./ProfilePanel/ProfileHeader";

import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addTab, setActiveTab } from "../../../../redux/slices/manageTeamSlice";
import { selectModuleTabs } from "../../../../redux/selectors/manageTeamTabSelector";
import { viewOrganizationById } from "../../../../features/ACS-Dashboard-Modules/Manage-Team/api";
import { resetReduxState } from "../../../../redux/store";

const UserProfileDropdown = React.forwardRef(({ open, onClose }, ref) => {
  const dropdownRef = useRef(null);
  const [userData, setUserData] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { tabs } = useSelector(selectModuleTabs("manageTeam"));

  // Load stored user info
  useEffect(() => {
    const stored = Cookies.get("userData");
    if (stored) {
      try {
        setUserData(JSON.parse(stored));
      } catch {}
    }
  }, []);

  // ------------------------------------------
  // VIEW FULL PROFILE
  // ------------------------------------------
  const handleOpenFullProfile = (user) => {
    if (!user?.email) return;

    navigate("/dashboard/manage-team");

    const id = `user-profile-${user.email}`;

    dispatch(
      addTab({
        id,
        name: `Profile: ${user.name || user.email}`,
        contentType: "viewUserProfile",
        data: user,
        isDynamic: true,
        createdAt: Date.now(),
      })
    );

    setTimeout(() => {
      dispatch(setActiveTab({ index: tabs.length }));
    }, 80);

    onClose?.();
  };

  // ------------------------------------------
  // VIEW ORGANIZATION — EXACTLY LIKE PROFILE
  // ------------------------------------------
  const handleOpenOrganization = async (org) => {
    if (!org?.orgId) return;

    navigate("/dashboard/manage-team");

    const res = await viewOrganizationById(org.orgId);

    dispatch(
      addTab({
        id: `org-${org.orgId}`,
        name: `Organization: ${org.organizationName || org.officeName}`,
        contentType: "viewPreferredPartnerDetails",
        data: res.data,
        isDynamic: true,
        createdAt: Date.now(),
      })
    );

    setTimeout(() => {
      dispatch(setActiveTab({ index: tabs.length }));
    }, 80);

    onClose?.();
  };

  const handleLogout = async () => {
    const token = Cookies.get("authToken");
    const officeCode = Cookies.get("officeCode");
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/user/logout`,
        {},
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(officeCode ? { "Office-Code": officeCode } : {}),
            "Content-Type": "application/json",
          },
        }
      );
    } catch (err) {
      // Continue local logout even when API logout fails.
      console.warn("Logout API call failed:", err);
    } finally {
      const cookieDomain = process.env.REACT_APP_COOKIE_DOMAIN;
      const cookieAttrs = { path: "/" };
      if (cookieDomain) cookieAttrs.domain = cookieDomain;

      Cookies.remove("authToken", cookieAttrs);
      Cookies.remove("userData", cookieAttrs);
      Cookies.remove("officeCode", cookieAttrs);
      Cookies.remove("officeCodes", cookieAttrs);

      resetReduxState();
      localStorage.removeItem("isCollapsed");
      localStorage.removeItem("allowedMenus");
      localStorage.removeItem("userPrivileges");
      localStorage.removeItem("menuTabStructure");
      localStorage.removeItem("organizationData");
      localStorage.removeItem("officeCode");
      localStorage.removeItem("organizationTypes");
      sessionStorage.clear();

      if (axios.defaults.headers.common.Authorization) {
        delete axios.defaults.headers.common.Authorization;
      }
      onClose?.();
      window.location.replace("/login");
    }
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !ref?.current?.contains(e.target)
      ) {
        onClose?.();
      }
    };
    document.addEventListener("mouseup", handler);
    return () => document.removeEventListener("mouseup", handler);
  }, [ref, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={dropdownRef}
          initial={{ opacity: 0, scale: 0.95, y: -8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -8 }}
          transition={{ duration: 0.18 }}
          className="
            absolute right-0 top-[calc(100%+1rem)]
            w-[clamp(30rem,45vw,38rem)]
            max-h-[78vh]
            bg-white dark:bg-acs-dark-surface
            shadow-2xl rounded-xl border border-gray-200 dark:border-acs-dark-border
            overflow-hidden z-[400]
            flex flex-col
          "
        >
          <ProfileHeader
            user={userData}
            onViewProfile={handleOpenFullProfile}
            onLogout={handleLogout}
          />

          <ProfilePage
            user={userData}
            onClose={onClose}
            onViewOrganization={handleOpenOrganization}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default UserProfileDropdown;
